import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Phone,
  Users,
  Queue,
  Scroll,
  Gear,
  ChartLine,
  SignOut,
  List,
  CaretLeft,
  CaretRight
} from '@phosphor-icons/react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: ChartLine },
  { path: '/dashboard/leads', label: 'Leads', icon: Users },
  { path: '/dashboard/queue', label: 'Call Queue', icon: Queue },
  { path: '/dashboard/scripts', label: 'Scripts', icon: Scroll },
  { path: '/dashboard/settings', label: 'Settings', icon: Gear },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {/* Sidebar */}
      <aside className={`bg-black border-r border-zinc-800 flex flex-col transition-all duration-300 ${collapsed ? 'w-16' : 'w-64'}`}>
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-zinc-800">
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="sidebar-logo">
            <div className="w-8 h-8 bg-blue-600 rounded-sm flex items-center justify-center flex-shrink-0">
              <Phone size={18} weight="fill" className="text-white" />
            </div>
            {!collapsed && <span className="text-lg font-semibold text-zinc-50">LeadCaller</span>}
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-2 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${isActive(item.path) ? 'active' : ''} ${collapsed ? 'justify-center' : ''}`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              title={collapsed ? item.label : undefined}
            >
              <item.icon size={20} weight={isActive(item.path) ? 'fill' : 'regular'} />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="mx-2 mb-2 p-2 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded-sm transition-colors"
          data-testid="sidebar-collapse-toggle"
        >
          {collapsed ? <CaretRight size={20} /> : <CaretLeft size={20} />}
        </button>

        {/* User section */}
        <div className="border-t border-zinc-800 p-4">
          {!collapsed && (
            <div className="mb-3">
              <p className="text-sm font-medium text-zinc-50 truncate">{user?.firstName} {user?.lastName}</p>
              <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
            </div>
          )}
          <button
            onClick={logout}
            className={`nav-item text-red-400 hover:text-red-300 hover:bg-red-500/10 w-full ${collapsed ? 'justify-center' : ''}`}
            data-testid="logout-button"
            title={collapsed ? 'Sign out' : undefined}
          >
            <SignOut size={20} />
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
}
